import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { kyrgyzstanObjectBookingsStore } from 'entities/kyrgyzstan'

import { useInfiniteScroll } from 'shared/hooks/use-infinity-scroll'

import { BookingCard } from '../booking-card'
import { BookingCardSkeleton } from '../booking-card-skeleton'

import s from './styles.module.scss'

interface Props {
  onHistoryClick: () => void
}

const BOOKING_SKELETON_COUNT = 2

export const ObjectBookingsWidget = observer(({ onHistoryClick }: Props) => {
  const { t } = useTranslation()
  const paginationTargetRef = useRef<HTMLDivElement>(null)
  const {
    bookings,
    clearBookings,
    error,
    fetchBookings,
    hasBookings,
    hasMore,
    isLoading,
    loadMoreBookings,
    shouldShowEmptyState,
    shouldShowInitialLoader,
    shouldShowPaginationLoader,
  } = kyrgyzstanObjectBookingsStore

  useEffect(() => {
    void fetchBookings(true)

    return () => {
      clearBookings()
    }
  }, [clearBookings, fetchBookings])

  useInfiniteScroll({
    targetRef: paginationTargetRef,
    canLoad: hasBookings && hasMore && !isLoading && !error,
    onLoad: loadMoreBookings,
    trigger: hasBookings,
  })

  return (
    <section
      className={s.section}
      aria-labelledby="object-bookings-title"
    >
      <div className={s.header}>
        <h2
          id="object-bookings-title"
          className={clsx('bold-16', s.title)}
        >
          {t('kyrgyzstan_block.my_objects.bookings.title')}
        </h2>

        <button
          type="button"
          className={clsx('semi-bold-14', s.history)}
          onClick={onHistoryClick}
        >
          {t('kyrgyzstan_block.my_objects.bookings.history')}
        </button>
      </div>

      {shouldShowInitialLoader && (
        <div className={s.list}>
          {Array.from({ length: BOOKING_SKELETON_COUNT }).map((_, index) => (
            <BookingCardSkeleton key={index} />
          ))}
        </div>
      )}

      {!shouldShowInitialLoader && bookings.length > 0 && (
        <div className={s.list}>
          {bookings.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
            />
          ))}

          {shouldShowPaginationLoader && <BookingCardSkeleton />}

          <div
            ref={paginationTargetRef}
            className={s.paginationTarget}
          />
        </div>
      )}

      {shouldShowEmptyState && (
        <p className={clsx('medium-14', s.empty)}>
          {t('kyrgyzstan_block.my_objects.bookings.empty')}
        </p>
      )}
    </section>
  )
})
